/**
 * TestPasswordModal — Marakin staff set a password for a made-up test account
 * (users.is_test_account) that belongs ONLY to test clubs (v51). 28 Oct 2026.
 * Backend: POST /api/platform/users/:id/test-password (rules in
 * backend/src/lib/staffPasswordTools.js; the server re-checks everything).
 * The password is never stored or shown anywhere after Save.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { X, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const MIN_LENGTH = 8; // backend/src/lib/password.js MIN_PASSWORD_LENGTH

export default function TestPasswordModal({ member, onClose }) {
  const { platformAxios } = usePlatformAuth();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const name = [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email;

  const save = useMutation({
    mutationFn: () => platformAxios.post(`/users/${member.user_id}/test-password`, { password }).then(r => r.data),
    onSuccess: () => { setPassword(''); setDone(true); },
    onError: (err) => setError(err.response?.data?.error || 'Could not set the password'),
  });

  const tooShort = password.length < MIN_LENGTH;
  const submit = (e) => {
    e.preventDefault();
    if (tooShort || save.isPending) return;
    setError('');
    save.mutate();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Set test password</h2>
          <button className="btn-close" onClick={onClose} aria-label="Close"><X size={24} /></button>
        </div>

        {done ? (
          <div className="modal-form">
            <div className="test-pw-done">
              <CheckCircle2 size={20} />
              <p>Password set. Their other sessions were signed out.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <form className="modal-form" onSubmit={submit}>
            {/* Tells password managers whose password this is (the TARGET's
                account, never the staffer's own). Not shown. */}
            <input type="text" name="username" autoComplete="username" value={member.email || ''} readOnly hidden />
            <p className="test-pw-intro">
              For <strong>{name}</strong>{member.email ? ` (${member.email})` : ''}. Only for made-up test accounts that
              belong to test clubs and nothing else. They aren't emailed about it.
            </p>
            {error && (
              <div className="error-box" style={{ marginBottom: 12 }}>
                <AlertCircle size={16} /><p>{error}</p>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="test-pw-input">New password</label>
              <div className="test-pw-field">
                <input
                  id="test-pw-input"
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button type="button" className="test-pw-toggle" onClick={() => setShow(s => !s)}
                  aria-label={show ? 'Hide password' : 'Show password'}>
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="test-pw-hint">At least {MIN_LENGTH} characters.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={tooShort || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
